/**
 * The only hostnames tracking scripts (GA4, Ahrefs) are allowed to actually fire on.
 *
 * There is no separate dev/staging GA4 property or Ahrefs project: local dev shares the
 * production GA4 measurement ID and Ahrefs key via .env/.env.local (see load-analytics.ts's own
 * comment), and there is no separate CDK stack for a PR-preview or staging deploy either (see
 * docs/knowledge-graph/10-infrastructure-deployment.md) — so a future preview/staging deploy of
 * this same Lambda code would ship with the same real credentials and fire real tracking with no
 * further guard. This hostname allowlist is that guard.
 *
 * Enforced client-side, inside the generated tracking scripts themselves, via
 * window.location.hostname — not server-side by environment/build mode — so it travels with the
 * script regardless of which server/environment rendered the HTML, and can't be bypassed by a
 * future call site that forgets to check `import.meta.env.DEV` or similar.
 */
export const PRODUCTION_HOSTNAMES = ['jobcrawls.com', 'www.jobcrawls.com'] as const;

/** True for the real production hostnames (exact match, not a substring/suffix check). */
export function isProductionHostname(hostname: string): boolean {
  return (PRODUCTION_HOSTNAMES as readonly string[]).includes(hostname);
}

/**
 * JS source fragment for splicing into a generated inline `<script>` string: returns early
 * (no-ops the rest of the enclosing function) unless the current page is on an allowlisted
 * production hostname. Must be used as a statement inside a function body (relies on `return`).
 */
export function buildProductionHostnameGuardSnippet(): string {
  return `if(${JSON.stringify(PRODUCTION_HOSTNAMES)}.indexOf(window.location.hostname)===-1)return;`;
}
